import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useReducedMotion } from "framer-motion";
import { Cake, CalendarClock, Check, Clock, Contact, GraduationCap, Inbox, Plus, Search, Sparkles, Upload, Users, Workflow, type LucideIcon } from "lucide-react";
import { ChannelMark, Header, InboxMock, ProfileMock, Stage, StageOptions } from "./FeatureMockups";
import "./feature-mockups.css";
import "./hero-features.css";

type ActionCard = {
  name: string; initials: string; detail: string; trigger: string; icon: LucideIcon; why: [string, string];
  channel: "wa" | "ig" | "tg"; draft: string; auto?: string; approved?: string;
};

// `auto` / `approved` are seconds into the scene when the card flips to "sent".
const actions: ActionCard[] = [
  { name: "Mei Lin Tan", initials: "ML", detail: "Parent of Kai, P5", trigger: "Birthday tomorrow", icon: Cake, why: ["Kai turns 11", "Replies on WhatsApp"], channel: "wa", draft: "Hi Mei Lin! Kai turns 11 tomorrow. Happy early birthday from all of us at Bright Minds! 🎂", auto: "3.6s" },
  { name: "Daniel Ong", initials: "DO", detail: "Retainer · Lumen Coffee", trigger: "Retainer renews in 14 days", icon: CalendarClock, why: ["Added 2 promos this quarter", "Approves quickly"], channel: "wa", draft: "Hi Daniel, your retainer renews on 14 Nov. Given the menu and Mid-Autumn work this quarter, shall I draft a renewal that includes seasonal promos?" },
  { name: "Wei Ling Tan", initials: "WT", detail: "Returning volunteer", trigger: "No shift in 60 days", icon: Clock, why: ["Packed meals monthly", "Prefers Saturday mornings"], channel: "wa", draft: "Hi Wei Ling, we've missed you on Saturday mornings! Two meal-packing spots are open this week. Shall I save one for you and your son?", approved: "6.2s" },
  { name: "Samantha Lee", initials: "SL", detail: "Parent of Chloe, P5", trigger: "Trial lesson yesterday", icon: GraduationCap, why: ["Asked about enrolment", "Sibling discount noted"], channel: "tg", draft: "Hi Samantha, how did Chloe find her first P5 Math lesson? I can enrol her for the term whenever you're ready, with the sibling discount." },
];

function ActionsMock() {
  return (
    <Stage>
      <div className="am-main">
        <Header>Actions</Header>
        <div className="am-bar">
          <span className="hf-act-seg"><b>All 4</b><span>Needs approval 3</span><span>Sent 1</span></span>
          <span className="am-spacer" />
          <span className="am-btn">Approve all</span>
        </div>
        <div className="hf-act-grid">
          {actions.map((card, index) => {
            const Icon = card.icon;
            const sentAt = card.auto ?? card.approved;
            return (
              <article className="hf-act" key={card.name} style={{ "--c": `${0.3 + index * 0.55}s`, "--s": sentAt ?? "99s" } as React.CSSProperties}>
                <header>
                  <span className="am-avatar">{card.initials}</span>
                  <div><b>{card.name}</b><small>{card.detail}</small></div>
                  <span className="hf-act-trigger"><Icon aria-hidden />{card.trigger}</span>
                </header>
                <div className="hf-act-why"><small>Why now</small><span><Sparkles aria-hidden />{card.why[0]}</span><span><Sparkles aria-hidden />{card.why[1]}</span></div>
                <div className="hf-act-draft">
                  <i className="hf-act-dots"><b /><b /><b /></i>
                  <p><ChannelMark ch={card.channel} size={14} />{card.draft}</p>
                </div>
                <footer>
                  <span className="hf-act-pending"><span className="am-btn am-btn--primary">Approve</span><span className="am-btn">Edit</span></span>
                  <span className="hf-act-sent"><Check aria-hidden />{card.auto ? "Sent automatically" : "Approved and sent"}</span>
                </footer>
              </article>
            );
          })}
        </div>
      </div>
    </Stage>
  );
}

type AiRow = { name: string; status: [string, string]; ch: "wa" | "ig" | "tg"; interest: string; time: string; sentiment: [string, string]; next: string; last: string };

const aiRows: AiRow[] = [
  { name: "Samantha Lee", status: ["At risk", "amber"], ch: "tg", interest: "P5 Math, sibling discount", time: "Saturday mornings", sentiment: ["Positive", "green"], next: "Send trial reminder", last: "Today" },
  { name: "Daniel Koh", status: ["Active", "green"], ch: "wa", interest: "Weekday evening classes", time: "Weekday evenings", sentiment: ["Positive", "green"], next: "Ask for a review", last: "2 days ago" },
  { name: "Aisha Rahman", status: ["New", "blue"], ch: "ig", interest: "PSLE intensive", time: "Sunday afternoons", sentiment: ["Neutral", "peach"], next: "Share the fees", last: "Yesterday" },
  { name: "Marcus Lim", status: ["Active", "green"], ch: "wa", interest: "JC2 H2 Math", time: "Weekday evenings", sentiment: ["Positive", "green"], next: "Book the trial", last: "3 days ago" },
  { name: "Nur Izzati", status: ["At risk", "amber"], ch: "ig", interest: "Sec 2 Math, weekly", time: "Saturday mornings", sentiment: ["Frustrated", "rose"], next: "Call back about fees", last: "1 week ago" },
  { name: "Ethan Wong", status: ["New", "blue"], ch: "tg", interest: "New student trial", time: "Any weekday", sentiment: ["Neutral", "peach"], next: "Send two trial slots", last: "Today" },
  { name: "Olivia Tan", status: ["Active", "green"], ch: "wa", interest: "Math Vision enrichment", time: "Sunday mornings", sentiment: ["Positive", "green"], next: "Offer term renewal", last: "4 days ago" },
  { name: "Hannah Teo", status: ["Dormant", "peach"], ch: "ig", interest: "Holiday workshops", time: "School holidays", sentiment: ["Neutral", "peach"], next: "Send holiday timetable", last: "2 months ago" },
  { name: "Lucas Chua", status: ["New", "blue"], ch: "wa", interest: "Science, P6", time: "Saturday afternoons", sentiment: ["Positive", "green"], next: "Hold a trial spot", last: "Today" },
  { name: "Mei Lin Tan", status: ["Active", "green"], ch: "wa", interest: "P5 Math options", time: "Weekday evenings", sentiment: ["Positive", "green"], next: "Confirm next term", last: "5 days ago" },
];

const channelCell = (row: AiRow, iconOnly = false) => (
  <td><span className="am-channel"><ChannelMark ch={row.ch} size={14} />{!iconOnly && { wa: "WhatsApp", ig: "Instagram", tg: "Telegram" }[row.ch]}</span></td>
);

const aiCols = ["interest", "time", "sentiment", "next"] as const;

function CrmAiMock() {
  // Phones: drop Status and move Channel after Next best step so every column title fits untruncated.
  const compact = (useContext(StageOptions)?.width ?? 1400) < 1000;
  const channelHead = <th>Channel</th>;
  return (
    <Stage>
      <div className="am-main">
        <Header>Customers</Header>
        <div className="am-bar">
          <span className="am-input"><Search aria-hidden /> Search customers...</span>
          <span className="am-spacer" />
          <span className="am-btn am-btn--primary"><Plus aria-hidden /> New customer</span>
          <span className="am-btn"><Upload aria-hidden /> Import customers</span>
        </div>
        <div className="am-content">
          <div className="hf-ai-banner"><Sparkles aria-hidden /><b>Filled in by Dibs</b><span>These columns are inferred from conversations. Nobody types them.</span></div>
          <table className="am-table hf-ai-table">
            {compact
              ? <colgroup><col style={{ width: 34 }} /><col style={{ width: "14%" }} /><col style={{ width: "21%" }} /><col style={{ width: "16%" }} /><col style={{ width: "14%" }} /><col style={{ width: "18%" }} /><col /></colgroup>
              : <colgroup><col style={{ width: 34 }} /><col style={{ width: "13%" }} /><col style={{ width: "9%" }} /><col style={{ width: "10%" }} /><col style={{ width: "17%" }} /><col style={{ width: "13%" }} /><col style={{ width: "10%" }} /><col style={{ width: "16%" }} /><col /></colgroup>}
            <thead>
              <tr>
                <th className="am-check"><i /></th><th>Name</th>{!compact && <><th>Status</th>{channelHead}</>}
                <th className="is-ai"><Sparkles aria-hidden />Interested in</th><th className="is-ai"><Sparkles aria-hidden />Best time</th><th className="is-ai"><Sparkles aria-hidden />Sentiment</th><th className="is-ai"><Sparkles aria-hidden />Next best step</th>
                {compact && channelHead}
                {!compact && <th>Last contact</th>}
              </tr>
            </thead>
            <tbody>
              {aiRows.map((row, rowIndex) => (
                <tr key={row.name}>
                  <td className="am-check"><i /></td>
                  <td className="am-name">{row.name}</td>
                  {!compact && <><td><span className={`am-chip am-chip--${row.status[1]}`}>{row.status[0]}</span></td>{channelCell(row)}</>}
                  {aiCols.map((col, colIndex) => (
                    <td className="hf-ai-cell" key={col} style={{ "--d": `${1.1 + rowIndex * 0.3 + colIndex * 0.14}s` } as React.CSSProperties}>
                      <i className="hf-skel" />
                      <span>{col === "sentiment" ? <span className={`am-chip am-chip--${row.sentiment[1]}`}>{row.sentiment[0]}</span> : row[col]}</span>
                    </td>
                  ))}
                  {compact && channelCell(row, true)}
                  {!compact && <td>{row.last}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Stage>
  );
}

const tabs: Array<{ id: string; label: string; icon: LucideIcon; note: string; rail: number; panel: () => ReactNode }> = [
  { id: "profiles", label: "Remembers everyone", icon: Contact, note: "3 new facts remembered from recent activities", rail: 1, panel: ProfileMock },
  { id: "inbox", label: "Listens everywhere", icon: Inbox, note: "Instagram and WhatsApp, matched to one customer", rail: 0, panel: InboxMock },
  { id: "crm", label: "Writes its own notes", icon: Users, note: "4 attributes filled in from conversations", rail: 1, panel: CrmAiMock },
  { id: "workflows", label: "Acts at the right moment", icon: Workflow, note: "4 follow-ups drafted, each with its reason", rail: 3, panel: ActionsMock },
];

export default function HeroFeatures() {
  const reduced = useReducedMotion();
  const stage = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [inView, setInView] = useState(false);
  const [manual, setManual] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const tab = tabs[index];
  const Panel = tab.panel;

  useEffect(() => {
    const node = stage.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Phones get a narrower design canvas so the product text stays legible instead of shrinking to ~25%.
  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const sync = () => setNarrow(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (reduced || manual || !inView) return;
    const timer = window.setTimeout(() => setIndex((current) => (current + 1) % tabs.length), 9500);
    return () => window.clearTimeout(timer);
  }, [index, inView, manual, reduced]);

  return (
    <div ref={stage} className="hf-stage" aria-label="Dibs product tour">
      <div className="hf-tabs" role="tablist" aria-label="Dibs features">
        {tabs.map(({ id, label, icon: Icon }, tabIndex) => (
          <button key={id} type="button" role="tab" id={`hf-tab-${id}`} aria-selected={index === tabIndex} aria-controls="hf-panel" className={index === tabIndex ? "is-active" : undefined} onClick={() => { setManual(true); setIndex(tabIndex); }}>
            <Icon aria-hidden />{label}
          </button>
        ))}
      </div>

      <div className="hf-window" role="tabpanel" id="hf-panel" aria-labelledby={`hf-tab-${tab.id}`}>
        <div className={`hf-panel hf-${tab.id}`} key={tab.id}>
          <StageOptions.Provider value={{ width: narrow ? (tab.id === "inbox" ? 560 : 760) : 1400, rail: tab.rail }}>
            <Panel />
          </StageOptions.Provider>
          <p className="hf-note"><Sparkles aria-hidden />{tab.note}</p>
        </div>
      </div>
    </div>
  );
}
